import { PLAYER_STATUS } from 'constants/gameConstants';
import { isExitedTable } from 'utils/recentlyExitedTables';

// Seat statuses that mean the player is genuinely AT a table, so their card
// must persist in "Your Tables" — including the transient away-states the strip
// deliberately shows (SITTING OUT / JOINING / STANDING UP / LEFT GAME), mirroring
// useActiveTables' SEAT_STATUS_LABELS.
//
// This set drives the lobby-derived active-table list, which re-subscribes to
// each table so state.tables repopulates on refresh/restart even when the
// engine's got_playerGames omits it (the client-side compensation documented in
// docs/investigations/20260512-omaha-tables-missing-after-refresh.md).
//
// Regression fixed: LEAVING (2 = STANDING UP) and EXIT_TABLE (10 = LEFT GAME)
// were missing, so a standing-up / left-game table was dropped from the derived
// list and never re-subscribed. Omaha survived via got_playerGames, but Texas —
// which the engine's 20s texas freshness filter also drops from got_playerGames
// — had NO restore path and vanished from "Your Tables" after restart. The
// EXIT_TABLE_* variants (17/18) were already present; the base LEAVING/EXIT_TABLE
// codes were the gap.
export const ACTIVE_AT_TABLE_STATUSES = new Set<number>([
  PLAYER_STATUS.ACTIVE, // 0
  PLAYER_STATUS.SITTING_OUT, // 1
  PLAYER_STATUS.LEAVING, // 2  — STANDING UP
  PLAYER_STATUS.SHOW_CARDS, // 3
  PLAYER_STATUS.POST_BLIND, // 4  — JOINING
  PLAYER_STATUS.WAIT_FOR_BB, // 5  — JOINING
  PLAYER_STATUS.FOR_SITOUT, // 6
  PLAYER_STATUS.FOR_CHARGING, // 7
  PLAYER_STATUS.SHOW_CARDS_FOR_SITOUT, // 8
  PLAYER_STATUS.EXIT_TABLE, // 10 — LEFT GAME
  PLAYER_STATUS.ADD_FUND, // 13
  PLAYER_STATUS.EXIT_TABLE_WAIT_FOR_BB, // 17 — LEFT GAME
  PLAYER_STATUS.EXIT_TABLE_POST_BLIND, // 18 — LEFT GAME
  PLAYER_STATUS.MUCK_CARDS, // 19
  PLAYER_STATUS.MUCK_CARDS_FOR_SITOUT // 20
]);

// True when the player's GUID occupies a seat whose status means they're still
// at the table (active, or an away-state we keep visible). Scans the up-to-10
// seat name/status fields on a lobby cash-game row.
export const isPlayerAtTable = (table: any, guid: string): boolean => {
  if (!table || !guid) return false;
  for (let i = 1; i <= 10; i++) {
    const seatGuid = table[`p${i}name`];
    const seatStatus = Number(table[`p${i}status`]);
    if (seatGuid && seatGuid === guid && ACTIVE_AT_TABLE_STATUSES.has(seatStatus)) {
      return true;
    }
  }
  return false;
};

/**
 * At the table (isPlayerAtTable) and not exited: not just left, and not exited
 * by the player without reopening it — even while their seat still reads LEFT
 * GAME, waiting for the engine to remove them. Drives the lobby-derived list,
 * which re-subscribes what it returns. Exported for unit testing.
 */
export const isAtTableAndNotExited = (table: any, guid: string): boolean =>
  isPlayerAtTable(table, guid) && !isExitedTable(String(table?.gameID));
